import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip
} from '@mui/material';
import {
  CheckCircle as SentIcon,
  ErrorOutline as FailedIcon,
  RemoveCircleOutline as SkippedIcon
} from '@mui/icons-material';

import { SKIP_REASON_LABELS } from '../../../services/w9Service';

const money = (amount) =>
  amount === null || amount === undefined
    ? '—'
    : `$${Number(amount).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })}`;

const SummaryCard = ({ icon: Icon, label, count, color }) => (
  <Card variant="outlined" sx={{ height: '100%' }}>
    <CardContent sx={{ textAlign: 'center' }}>
      <Box component={Icon} sx={{ fontSize: 32, color, mb: 0.5 }} />
      <Typography variant="h4" fontWeight={700} sx={{ lineHeight: 1.1 }}>
        {count}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
    </CardContent>
  </Card>
);

/**
 * Per-recipient outcome of a completed campaign.
 *
 * Skipped rows are contractors who qualified but were never emailable, so they
 * carry a reason rather than a message id.
 */
const CampaignResults = ({ result }) => {
  if (!result) return null;

  const { campaign, results, sentAt, sentBy, campaignId } = result;
  const rows = [
    ...results.sent.map((r) => ({ ...r, outcome: 'sent' })),
    ...results.failed.map((r) => ({ ...r, outcome: 'failed' })),
    ...results.skipped.map((r) => ({ ...r, outcome: 'skipped' }))
  ];

  return (
    <Box>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <SummaryCard
            icon={SentIcon}
            label="Sent"
            count={campaign.sent}
            color="success.main"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <SummaryCard
            icon={FailedIcon}
            label="Failed"
            count={campaign.failed}
            color={campaign.failed ? 'error.main' : 'text.disabled'}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <SummaryCard
            icon={SkippedIcon}
            label="Skipped"
            count={campaign.skipped}
            color={campaign.skipped ? 'warning.main' : 'text.disabled'}
          />
        </Grid>
      </Grid>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Tax year {campaign.taxYear} · due {campaign.dueDate}
        {sentBy ? ` · sent by ${sentBy}` : ''}
        {sentAt ? ` · ${new Date(sentAt).toLocaleString()}` : ''}
        {campaignId ? ` · campaign ${campaignId}` : ''}
      </Typography>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Contractor</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>
                Amount
              </TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Detail</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={`${r.outcome}-${r.email || r.name}`} hover>
                <TableCell>{r.name}</TableCell>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                  {r.email || '—'}
                </TableCell>
                <TableCell align="right" sx={{ fontFamily: 'monospace' }}>
                  {money(r.amount)}
                </TableCell>
                <TableCell>
                  {r.outcome === 'sent' && <Chip size="small" color="success" label="Sent" />}
                  {r.outcome === 'failed' && <Chip size="small" color="error" label="Failed" />}
                  {r.outcome === 'skipped' && (
                    <Chip size="small" color="warning" label="Skipped" />
                  )}
                </TableCell>
                <TableCell
                  sx={{
                    fontFamily: r.outcome === 'sent' ? 'monospace' : 'inherit',
                    fontSize: '0.75rem',
                    color: r.outcome === 'failed' ? 'error.main' : 'text.secondary',
                    maxWidth: 280,
                    wordBreak: 'break-word'
                  }}
                >
                  {r.outcome === 'sent' && (r.messageId || 'Delivered')}
                  {r.outcome === 'failed' && (r.error || 'Unknown error')}
                  {r.outcome === 'skipped' &&
                    (SKIP_REASON_LABELS[r.reason] || r.reason || 'Not emailable')}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default CampaignResults;
